/**
 * Footer Component
 *
 * Comprehensive footer component based on Figma design
 * Includes newsletter signup and footer links sections
 * Reusable across the application
 * Mobile responsive with horizontal link layout
 */

'use client';

import React, { useState, FormEvent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ChevronDownIcon, CopyIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  typographyColors,
  brandColors,
  backgroundColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { footerContactItems } from '@/constants/footerData';

interface FooterProps {
  onJoinCommunity?: (email: string) => void;
  className?: string;
}

export function Footer({ onJoinCommunity, className = '' }: FooterProps) {
  const [email, setEmail] = useState('');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const router = useRouter();

  const copyToClipboard = async (value: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Copied successfully', {
        position: 'top-right',
        style: {
          color: '#2f6b3b',
        },
      });
    } catch (error) {
      toast.error('Copy failed');
      console.error('Footer copy failed:', error);
    }
  };

  // Log footer render
  React.useEffect(() => {
    console.log('🦶 Footer: Footer component rendered');
  }, []);

  // Hide "Join Our Community" section on all screens
  const showJoinCommunity = false;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onJoinCommunity && email.trim()) {
      onJoinCommunity(email.trim());
      setEmail('');
    }
  };

  // Handle experience link navigation with scroll to top
  const handleExperienceLinkClick = (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.TouchEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeof window !== 'undefined') {
      // Clear any saved scroll positions
      sessionStorage.removeItem('packages-scroll-position');
      sessionStorage.removeItem('activities-scroll-position');

      // Scroll to top immediately (before navigation)
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Navigate to the route
      router.push(href);

      // Ensure scroll to top after navigation completes
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });

      // Also scroll after a short delay to catch any layout changes
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  };

  // Background pattern - can be replaced with actual image path
  // Format: url(<path-to-image>) lightgray 0% 0% / 100px 100px repeat, #FFF
  const backgroundPattern = `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 40, 50 50 T 100 50' stroke='%23d3d3d3' fill='none' stroke-width='1'/%3E%3C/svg%3E") lightgray 0% 0% / 100px 100px repeat, ${brandColors.tertiary}`;

  return (
    <footer
      className={className}
      style={{
        background: backgroundPattern,
        position: 'relative',
        overflow: 'hidden',
        paddingTop: spacing[6],
      }}
    >
      {/* Main Container */}
      <div
        className='flex flex-col items-center relative z-10 px-4 md:px-20'
        style={{
          gap: spacing[12],
        }}
      >
        {/* Newsletter/Community Signup Section */}
        {showJoinCommunity && (
          <div
            className='flex flex-col items-center w-full'
            style={{
              maxWidth: '800px',
            }}
          >
            <div
              className='flex flex-col items-center'
              style={{
                gap: spacing[0],
                marginBottom: spacing[8],
              }}
            >
              <h2
                className='text-center capitalize text-[30px] md:text-[48px]'
                style={{
                  color: typographyColors.primary,
                  lineHeight: 'normal',
                  letterSpacing: '-0.5px',
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.bold,
                }}
              >
                Join Our
              </h2>
              <h3
                className='text-center text-[30px] md:text-[48px]'
                style={{
                  color: typographyColors.primary,
                  lineHeight: 'normal',
                  letterSpacing: '-0.5px',
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.light,
                }}
              >
                Community Of Explorers
              </h3>
            </div>

            <form
              onSubmit={handleSubmit}
              className='flex flex-row items-center w-full'
              style={{
                maxWidth: '600px',
                gap: spacing[2],
              }}
            >
              <div
                className='flex-1 flex items-center rounded-lg border'
                style={{
                  backgroundColor: backgroundColors.primary,
                  borderColor: '#d6d3d1',
                  paddingTop: '14px',
                  paddingBottom: '14px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  gap: '10px',
                }}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10.0013 10.0001C12.3025 10.0001 14.168 8.13464 14.168 5.83341C14.168 3.53223 12.3025 1.66675 10.0013 1.66675C7.70013 1.66675 5.83465 3.53223 5.83465 5.83341C5.83465 8.13464 7.70013 10.0001 10.0013 10.0001Z'
                    stroke='#78716C'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M17.1596 18.3334C17.1596 15.1084 13.9513 12.5001 10.0013 12.5001C6.05127 12.5001 2.84294 15.1084 2.84294 18.3334'
                    stroke='#78716C'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <input
                  type='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder='Enter your email'
                  className='flex-1 outline-none placeholder:text-[#78716c]'
                  style={{
                    backgroundColor: 'transparent',
                    color: typographyColors.primary,
                    fontSize: typography.size.sm,
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.regular,
                  }}
                  required
                />
              </div>
              <button
                type='submit'
                className='rounded-lg whitespace-nowrap'
                style={{
                  backgroundColor: brandColors.primary,
                  color: typographyColors.primary,
                  fontSize: typography.size.sm,
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.semibold,
                  paddingTop: '14px',
                  paddingBottom: '14px',
                  paddingLeft: spacing[6],
                  paddingRight: spacing[6],
                }}
              >
                Join Us
              </button>
            </form>
          </div>
        )}

        {/* Footer Links Section */}
        <div
          className='w-full'
          style={{
            paddingBottom: spacing[12],
          }}
        >
          <div className='max-w-[1400px] mx-auto'>
            {/* Footer Links Container */}
            <div
              className='flex flex-col md:flex-row items-start'
              style={{
                gap: spacing[6],
              }}
            >
              {/* Left Column - Logo and Social */}
              <div
                className='w-full md:max-w-[329px]'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[6],
                }}
              >
                {/* Logo and Description */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[2],
                  }}
                >
                  <div style={{ width: '109px', height: '36px' }}>
                    <Image
                      src='/assets/images/logo.svg'
                      alt='Nexgo'
                      width={109}
                      height={36}
                      className='h-full w-full object-contain'
                      unoptimized
                    />
                  </div>
                  <p
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: typography.weight.regular,
                      fontSize: typography.size.base,
                      lineHeight: 'normal',
                      color: typographyColors.primary,
                      margin: 0,
                    }}
                  >
                    Nexgo is the travel intelligence companion for those who see
                    travel as healing, growth, and transformation.
                  </p>
                </div>

                {/* Social Links */}
                <div
                  style={{
                    display: 'flex',
                    gap: spacing[3],
                  }}
                >
                  {/* Instagram */}
                  <a
                    href='https://www.instagram.com/nexgo.ai?igsh=MWZuMzJpeHYyNDNsbw%3D%3D&utm_source=qr'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      border: '1px solid rgba(4, 5, 5, 0.24)',
                      borderRadius: '100px',
                      padding: spacing[2],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      textDecoration: 'none',
                      backgroundColor: backgroundColors.primary,
                    }}
                    className='hover:opacity-80 transition-opacity'
                  >
                    {/* Instagram Icon from public/assets/images/instagram.svg */}
                    <Image
                      src='/assets/images/icons/instagram-logo-fill.svg'
                      alt='Instagram'
                      width={20}
                      height={20}
                      className='h-full w-full object-contain'
                      unoptimized
                    />
                  </a>
                  {/* Facebook */}
                  <a
                    href='https://www.facebook.com/share/1D9jXU37zB/?mibextid=LQQJ4d'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      border: '1px solid rgba(4, 5, 5, 0.24)',
                      borderRadius: '100px',
                      padding: spacing[2],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '36px',
                      height: '36px',
                      textDecoration: 'none',
                      backgroundColor: backgroundColors.primary,
                    }}
                    className='hover:opacity-80 transition-opacity'
                  >
                    <Image
                      src='/assets/images/icons/facebook-logo-fill.svg'
                      alt='Facebook'
                      width={20}
                      height={20}
                      className='h-full w-full object-contain'
                      unoptimized
                    />
                  </a>
                </div>
              </div>

              {/* Right Columns - Links */}
              <div className='flex-1 flex flex-col md:flex-row justify-end w-full md:w-auto gap-12 md:gap-24'>
                {/* Experiences Column */}
                <div
                  className='w-full md:w-auto'
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[4],
                  }}
                >
                  <h3
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: typography.weight.medium,
                      fontSize: typography.size.lg,
                      color: typographyColors.primary,
                      margin: 0,
                    }}
                  >
                    Experiences
                  </h3>
                  {/* Desktop: Vertical list */}
                  <div
                    className='hidden md:flex flex-col'
                    style={{ gap: spacing[4] }}
                  >
                    <Link
                      href='/experiences?type=nexgo-journeys'
                      onClick={e =>
                        handleExperienceLinkClick(
                          e,
                          '/experiences?type=nexgo-journeys'
                        )
                      }
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Nexgo Journeys
                      </p>
                    </Link>
                    <Link
                      href='/experiences?type=stays'
                      onClick={e =>
                        handleExperienceLinkClick(e, '/experiences?type=stays')
                      }
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Nexgo Spaces
                      </p>
                    </Link>
                    <Link
                      href='/experiences?type=activities'
                      onClick={e =>
                        handleExperienceLinkClick(
                          e,
                          '/experiences?type=activities'
                        )
                      }
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Guided Activities
                      </p>
                    </Link>
                    <Link
                      href='/riders-corner'
                      onClick={e =>
                        handleExperienceLinkClick(e, '/riders-corner')
                      }
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Rider&apos;s Corner
                      </p>
                    </Link>
                  </div>
                  {/* Mobile: Horizontal with wrapping */}
                  <div
                    className='flex md:hidden flex-wrap items-center'
                    style={{ gap: spacing[4] }}
                  >
                    <Link
                      href='/experiences?type=nexgo-journeys'
                      onClick={e =>
                        handleExperienceLinkClick(
                          e,
                          '/experiences?type=nexgo-journeys'
                        )
                      }
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Nexgo Journeys
                      </span>
                    </Link>
                    <Link
                      href='/experiences?type=stays'
                      onClick={e =>
                        handleExperienceLinkClick(e, '/experiences?type=stays')
                      }
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Nexgo Spaces
                      </span>
                    </Link>
                    <Link
                      href='/experiences?type=activities'
                      onClick={e =>
                        handleExperienceLinkClick(
                          e,
                          '/experiences?type=activities'
                        )
                      }
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Guided Activities
                      </span>
                    </Link>
                    <Link
                      href='/riders-corner'
                      onClick={e =>
                        handleExperienceLinkClick(e, '/riders-corner')
                      }
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Rider&apos;s Corner
                      </span>
                    </Link>
                  </div>
                </div>

                {/* Quick Links Column */}
                <div
                  className='w-full md:w-auto'
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[6],
                  }}
                >
                  <h3
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: typography.weight.medium,
                      fontSize: typography.size.lg,
                      color: typographyColors.primary,
                      margin: 0,
                    }}
                  >
                    Quick Links
                  </h3>
                  {/* Desktop: Vertical list */}
                  <div
                    className='hidden md:flex flex-col'
                    style={{ gap: spacing[4] }}
                  >
                    <a
                      href='https://nexgoai.web.app/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        About Us
                      </p>
                    </a>
                    <a
                      href='https://blogs.nexgo.ai/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <p
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          margin: 0,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Blogs
                      </p>
                    </a>
                    <div>
                      <button
                        type='button'
                        onClick={() => setIsContactOpen(prev => !prev)}
                        className='w-full flex items-center gap-2 hover:opacity-80 transition-opacity'
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                        }}
                      >
                        <span>Contact Us</span>
                        <ChevronDownIcon
                          className={`transition-transform duration-300 ${isContactOpen ? 'rotate-180' : ''}`}
                          size={16}
                        />
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${isContactOpen ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
                      >
                        {footerContactItems.map((item, index) => {
                          const ContactIcon = item.icon;
                          return (
                            <div
                              key={item.contactField}
                              className='flex items-center justify-start gap-2'
                              style={{
                                margin: index === 0 ? 0 : `${spacing[3]} 0 0 0`,
                                background: 'transparent',
                              }}
                            >
                              <span style={{ color: typographyColors.primary }}>
                                <ContactIcon size={14} />
                              </span>
                              <a
                                href={item.hrefAction}
                                className='hover:opacity-80 transition-opacity'
                                style={{
                                  fontFamily: fontPrimary,
                                  fontWeight: typography.weight.regular,
                                  fontSize: typography.size.sm,
                                  color: '#2563eb',
                                  textDecoration: 'underline',
                                }}
                              >
                                {item.contactField}
                              </a>
                              <button
                                type='button'
                                aria-label={item.copyAriaLabel}
                                onClick={() =>
                                  copyToClipboard(item.contactField)
                                }
                                className='hover:opacity-80 transition-opacity'
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: typographyColors.primary,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Mobile: Horizontal with wrapping */}
                  <div
                    className='flex md:hidden flex-wrap items-center'
                    style={{ gap: spacing[4] }}
                  >
                    <a
                      href='https://nexgoai.web.app/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        About Us
                      </span>
                    </a>
                    <a
                      href='https://blogs.nexgo.ai/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <span
                        style={{
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                          cursor: 'pointer',
                        }}
                        className='hover:opacity-80 transition-opacity'
                      >
                        Blogs
                      </span>
                    </a>
                    <div className='w-full'>
                      <button
                        type='button'
                        onClick={() => setIsContactOpen(prev => !prev)}
                        className='w-full flex items-center gap-2 hover:opacity-80 transition-opacity'
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          fontFamily: fontPrimary,
                          fontWeight: typography.weight.regular,
                          fontSize: typography.size.sm,
                          color: typographyColors.primary,
                        }}
                      >
                        <span>Contact Us</span>
                        <ChevronDownIcon
                          className={`transition-transform duration-300 ${isContactOpen ? 'rotate-180' : ''}`}
                          size={16}
                        />
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out gap-2 overflow-hidden ${isContactOpen ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
                      >
                        {footerContactItems.map((item, index) => {
                          const ContactIcon = item.icon;
                          return (
                            <div
                              key={item.contactField}
                              className='flex items-center gap-2'
                              style={{
                                margin: index === 0 ? 0 : `${spacing[3]} 0 0 0`,
                                background: 'transparent',
                              }}
                            >
                              <span style={{ color: typographyColors.primary }}>
                                <ContactIcon size={14} />
                              </span>
                              <a
                                href={item.hrefAction}
                                className='hover:opacity-80 transition-opacity'
                                style={{
                                  fontFamily: fontPrimary,
                                  fontWeight: typography.weight.regular,
                                  fontSize: typography.size.sm,
                                  color: '#2563eb',
                                  textDecoration: 'underline',
                                }}
                              >
                                {item.contactField}
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div
        className='w-full flex flex-col items-center justify-center'
        style={{
          backgroundColor: brandColors.primary,
          paddingTop: '20px',
          paddingBottom: '20px',
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
        }}
      >
        {/* Desktop & Mobile: Left/Right layout */}
        <div
          className='flex flex-col-reverse md:flex-row items-center justify-between w-full'
          style={{
            maxWidth: '1400px',
            gap: spacing[4],
          }}
        >
          {/* Copyright Text */}
          <p
            className='text-center md:text-left whitespace-nowrap'
            style={{
              color: typographyColors.primary,
              fontFamily: 'Poppins, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '100%',
              letterSpacing: '0%',
              margin: 0,
            }}
          >
            © 2025 Nexgo AI Labs Inc. All rights reserved.
          </p>

          {/* Terms and Privacy Links */}
          <div
            className='flex items-center justify-center'
            style={{
              gap: spacing[2],
            }}
          >
            <a
              href='https://nexgo.ai/terms'
              target='_blank'
              rel='noopener noreferrer'
            >
              <span
                className='cursor-pointer hover:opacity-80 transition-opacity text-center'
                style={{
                  color: typographyColors.primary,
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '100%',
                }}
              >
                Terms of Service
              </span>
            </a>
            <span
              className='md:hidden'
              style={{
                color: typographyColors.primary,
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '100%',
              }}
            >
              •
            </span>
            <a
              href='https://nexgo.ai/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
            >
              <span
                className='cursor-pointer hover:opacity-80 transition-opacity text-center'
                style={{
                  color: typographyColors.primary,
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '100%',
                }}
              >
                Privacy Policy
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
